
import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogTrigger } from '@/components/ui/dialog'
import { Plus } from 'lucide-react'
import { useModuleManagement } from '@/hooks/useModuleManagement'
import type { LearnModule } from '@/types/module'
import ModuleFormDialog from './ModuleFormDialog'
import ModulesTable from './ModulesTable'

const AdminModules = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingModule, setEditingModule] = useState<LearnModule | null>(null)
  
  const { 
    modules, 
    loading, 
    createModule, 
    updateModule, 
    deleteModule 
  } = useModuleManagement()

  const handleCreate = async (formData: any) => {
    const success = await createModule(formData)
    if (success) {
      setIsCreateOpen(false)
    }
    return success
  }

  const handleUpdate = async (formData: any) => {
    if (!editingModule) return false
    const success = await updateModule(editingModule.id, formData)
    if (success) {
      setEditingModule(null)
    }
    return success
  }

  const handleEdit = (module: LearnModule) => {
    console.log('Editing module:', module)
    setEditingModule(module)
  }

  const handleDelete = async (moduleId: string) => {
    console.log('Deleting module:', moduleId)
    await deleteModule(moduleId)
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <p className="text-gray-600">Loading modules...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Learn Modules Management</CardTitle>
            <CardDescription>Create and manage learning modules for students</CardDescription>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Module
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <ModulesTable 
          modules={modules}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        <ModuleFormDialog
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSubmit={handleCreate}
          title="Create New Learn Module"
          description="Add a new learning module for students"
        />

        <ModuleFormDialog
          isOpen={!!editingModule}
          onClose={() => setEditingModule(null)}
          onSubmit={handleUpdate}
          editingModule={editingModule}
          title="Edit Learn Module"
          description="Update module information"
        />
      </CardContent>
    </Card>
  )
}

export default AdminModules
